import { Inject, Injectable, Signal, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Observable,
  of,
  timer,
  Subscription,
  throwError,
  concatMap,
  catchError,
  map,
  take,
  tap,
  shareReplay,
  retryWhen,
  finalize,
} from 'rxjs';
import { RequestService } from '@services/http/request/request.service';
import { APP_CONFIG, AppConfig } from '@src/app.config';
import { StorageService } from '@services/core/storage/storage.service';
import { hasValue } from '@shared/utils/helpers';

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  /** seconds since epoch or ms since epoch (defensive) */
  expires: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  // ─────────────── signals (primary API) ───────────────
  /** Live access token signal. Null when logged out/invalid. */
  readonly accessToken$: Signal<string | null>;
  readonly authState$: Signal<boolean>;

  // internal writable signals
  private readonly _accessToken = signal<string | null>(null);
  private readonly _authState = signal<boolean>(false);

  // keep old getter for compatibility
  get accessToken(): string | null {
    return this._accessToken();
  }

  // storage
  private readonly tokenKey = 'tokenResponse';
  private refreshInProgress = false;

  // refresh scheduling
  private readonly REFRESH_OFFSET_MS = 30_000; // 30 seconds before expiry
  private refreshTimerSub: Subscription | null = null;

  // backoff for refresh retries: 3s, 5s, 10s, 15s, 30s
  private readonly REFRESH_BACKOFF_MS = [3_000, 5_000, 10_000, 15_000, 30_000];

  constructor(
    @Inject(APP_CONFIG) private appCfg: AppConfig,
    private readonly req: RequestService,
    private readonly storage: StorageService,
    private readonly destroyRef: DestroyRef,
  ) {
    // expose read-only signals
    this.accessToken$ = computed(() => this._accessToken());
    this.authState$ = computed(() => this._authState());

    // restore token to mem
    this.loadingTokenToMemory();

    // 1) validate token once on init
    // 2) then set up timer for 30s before expiry
    this.ensureTokenValid()
      .pipe(take(1))
      .subscribe(() => this.setupRefreshTimer());
  }

  // ───────────────────────────────────────── login/logout ─────────────────────
  login(username: string, password: string): Observable<any> {
    const url = `${this.appCfg.angularEndpoint}/auth/login`;
    return this.req.post(url, { username, password }).pipe(
      take(1),
      map(res => {
        const body: TokenResponse = res.body;
        if (hasValue(body)) {
          this.storeToken(body);
          this._authState.set(true);
          // new login → schedule refresh based on this token
          this.setupRefreshTimer(body);
          return true;
        }
        return false;
      }),
      catchError(() => of(false)),
    );
  }

  logout(): void {
    this.clearRefreshTimer();
    this.clearToken();
    this._authState.set(false);
  }

  // ───────────────────────────── helpers (signals-first) ──────────────────────
  private storeToken(t: TokenResponse): void {
    this._accessToken.set(t.accessToken);
    this.storage.setItem(this.tokenKey, t);
  }

  private loadingTokenToMemory(): void {
    const t = this.getTokenResponse();
    if (!t) return;
    if (this.tokenExpired(t)) {
      this._accessToken.set(null);
      this._authState.set(false);
    } else {
      this._accessToken.set(t.accessToken);
      this._authState.set(true);
    }
  }

  private clearToken(): void {
    this._accessToken.set(null);
    this.storage.removeItem(this.tokenKey);
  }

  private getTokenResponse(): TokenResponse | null {
    const t = this.storage.getItem<TokenResponse>(this.tokenKey);
    return t && typeof t !== 'string' ? t : null;
  }

  private tokenExpired(t: TokenResponse): boolean {
    const expSec = t.expires > 1e12 ? Math.floor(t.expires / 1000) : t.expires; // ms→s guard
    const nowSec = Math.floor(Date.now() / 1000);
    // treat "expiring within 30s" as expired
    return expSec - nowSec <= 30;
  }

  // ──────────────────────── refresh timer management ──────────────────────────
  private clearRefreshTimer(): void {
    if (this.refreshTimerSub) {
      this.refreshTimerSub.unsubscribe();
      this.refreshTimerSub = null;
    }
  }

  /**
   * Schedule a one-shot timer to refresh the token 30s before expiry.
   * If the token is already expired / within 30s, it refreshes immediately
   * and then re-schedules based on the new token.
   */
  private setupRefreshTimer(fromToken?: TokenResponse): void {
    const t = fromToken ?? this.getTokenResponse();
    if (!t) {
      this.clearRefreshTimer();
      return;
    }

    // normalise to ms
    const expMs = t.expires > 1e12 ? t.expires : t.expires * 1000;
    const nowMs = Date.now();
    const delayMs = expMs - nowMs - this.REFRESH_OFFSET_MS;

    // if we're already inside the 30s window, refresh immediately
    if (delayMs <= 0) {
      this.ensureTokenValid()
        .pipe(take(1))
        .subscribe(() => {
          // after refresh (if successful), re-read and schedule again
          this.setupRefreshTimer();
        });
      return;
    }

    // clear any existing timer and set a new one
    this.clearRefreshTimer();

    this.refreshTimerSub = timer(delayMs)
      .pipe(
        take(1),
        concatMap(() => this.ensureTokenValid().pipe(take(1))),
        tap(() => {
          // after validation/refresh, schedule the next refresh window
          this.setupRefreshTimer();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // ───────────────────────────── auto-refresh core ────────────────────────────
  // returns a boolean whether or not the token is valid
  private ensureTokenValid(): Observable<boolean> {
    const current = this.getTokenResponse();

    // no tokens stored, return invalid
    if (!current) {
      this._authState.set(false);
      if (this._accessToken() !== null) this._accessToken.set(null);
      this.clearRefreshTimer();
      return of(false);
    }

    // token not expired, skip refresh
    if (!this.tokenExpired(current)) {
      if (this._accessToken() !== current.accessToken) {
        this._accessToken.set(current.accessToken);
      }
      this._authState.set(true);
      return of(true);
    }

    // already refreshing → return current state
    if (this.refreshInProgress) {
      return of(this._authState());
    }

    this.refreshInProgress = true;

    return this.refreshTokenWithBackoff(current.refreshToken).pipe(
      finalize(() => {
        this.refreshInProgress = false;
      }),
    );
  }

  /**
   * Performs the refresh HTTP call with retry + backoff.
   * Returns true if we end up with a valid token, false if all retries fail.
   */
  private refreshTokenWithBackoff(refreshToken: string): Observable<boolean> {
    const url = `${this.appCfg.baseApiEndpoint}/app/auth/refreshToken`;

    return this.req.post(url, { token: refreshToken }).pipe(
      take(1),
      tap(res => {
        const body: TokenResponse = res.body;
        if (body) {
          // success path: store new token, keep user logged in
          this.storeToken(body);
          this._authState.set(true);
          // refresh succeeded → reschedule timer with new expiry
          this.setupRefreshTimer(body);
        } else {
          // malformed response → treat as hard failure
          this.clearToken();
          this._authState.set(false);
          this.clearRefreshTimer();
          throw new Error('Empty refresh response body');
        }
      }),
      map(() => this._authState()),
      // Retry with backoff on any error
      retryWhen(errors =>
        errors.pipe(
          concatMap((error, index) => {
            if (index >= this.REFRESH_BACKOFF_MS.length) {
              // exceeded retries → propagate error to final catchError
              return throwError(() => error);
            }
            const delayMs = this.REFRESH_BACKOFF_MS[index];
            console.warn(
              `[AuthService] refreshToken failed (attempt ${index + 1}), retrying in ${delayMs}ms`,
            );
            return timer(delayMs);
          }),
        ),
      ),
      // Final failure after all retries
      catchError(err => {
        console.error('[AuthService] refreshToken failed after retries:', err);
        this.clearToken();
        this._authState.set(false);
        this.clearRefreshTimer();
        return of(false);
      }),
    );
  }

  checkLoggedIn() {
    return this.ensureTokenValid().pipe(take(1), shareReplay(1));
  }
}
